import React from "react";

/**
 * KickoffRouteBoundary (kickoff refinement A).
 *
 * The kickoff route is a lazy chunk. When that import rejects (a chunk the
 * deployment no longer serves, or an HTML page served where the script was
 * expected), React had no boundary to catch it, so the whole app unmounted and
 * the learner was left on a black page. This boundary wraps the kickoff route
 * element only, inside the app's existing Suspense, and renders a panel with
 * two ways forward.
 *
 * NO REPAIR IS CLAIMED. Reload fetches the page again, which is the only thing
 * that can pick up a newer deployment; if it fails again the panel renders
 * again. Back is a FULL navigation, because the rejected lazy import is cached
 * for the life of this page and a client-side route change cannot clear it.
 *
 * It is imported statically by the router, so it cannot itself be the chunk
 * that failed. Tailwind only, for the same reason: it must not depend on the
 * kickoff chunk's stylesheet.
 */
class KickoffRouteBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error) {
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.warn("[KickoffRouteBoundary] the kickoff route failed", error);
    }
  }

  render() {
    const { failed } = this.state;
    const { children } = this.props;
    if (!failed) return children;
    return (
      <div
        className="fixed inset-0 z-[9999] flex flex-col items-center justify-center gap-6 bg-blackpearl px-4 text-center font-afacad"
        data-testid="kickoff-route-failed"
        role="alert"
      >
        <p className="text-[22px] leading-snug text-text-lv0">
          couldn&apos;t open the kickoff.
        </p>
        <div className="flex flex-wrap items-center justify-center gap-3">
          <button
            type="button"
            className="min-h-[44px] rounded-full bg-persimmon px-6 text-sm font-semibold text-blackpearl focus-ring"
            onClick={() => window.location.reload()}
          >
            reload
          </button>
          <button
            type="button"
            className="min-h-[44px] rounded-full border border-white/25 px-6 text-sm text-text-lv2 hover:bg-white/5 focus-ring"
            onClick={() => window.location.assign("/dashboard")}
          >
            back to dashboard
          </button>
        </div>
      </div>
    );
  }
}

export default KickoffRouteBoundary;
